import React, { useState, useEffect, useCallback } from 'react';
import {
  IoAddOutline,
  IoTrashOutline,
  IoCreateOutline,
  IoReorderThreeOutline,
  IoFlashOutline,
  IoLocationOutline,
  IoCloseOutline,
  IoSaveOutline,
  IoRefreshOutline,
  IoCheckmarkOutline,
  IoArrowUndoOutline,
} from 'react-icons/io5';
import { routesAPI, subRoutesAPI, salesPointsAPI, depotsAPI } from '../services/api';
import styles from './RoutesPanel.module.css';
import SalespointPicker from './SalespointPicker';

const ROUTE_TYPES = [
  { value: 'delivery', label: 'Delivery Route' },
  { value: 'sales', label: 'Sales Route' },
  { value: 'collection', label: 'Collection Route' },
  { value: 'mixed', label: 'Mixed Route' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

const BRANCH_TYPES = [
  { value: 'spur', label: 'Spur (dead-end)' },
  { value: 'mainline_rejoin', label: 'Mainline Rejoin' },
  { value: 'branch_connector', label: 'Branch Connector' },
];

const ROUTE_COLORS = [
  '#FF5252', // Red
  '#2196F3', // Blue
  '#4CAF50', // Green
  '#FF9800', // Orange
  '#9C27B0', // Purple
  '#00BCD4', // Cyan
  '#FFEB3B', // Yellow
  '#E91E63', // Pink
  '#009688', // Teal
  '#FF5722', // Deep Orange
  '#3F51B5', // Indigo
  '#8BC34A', // Light Green
];


export default function RoutesPanel({ onRouteSelect, selectedRouteIds = [] }) {
  // State
  const [routes, setRoutes] = useState([]);
  const [salesPoints, setSalesPoints] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // UI State
  const [expandedRouteId, setExpandedRouteId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  
  // Drag and drop state
  const [draggedWaypoint, setDraggedWaypoint] = useState(null);
  const [reorderedWaypoints, setReorderedWaypoints] = useState({});
  const [pendingReorder, setPendingReorder] = useState({});
  
  // Saved waypoint order snapshots (for reverting after optimize)
  const [savedWaypointOrders, setSavedWaypointOrders] = useState({});

  // Waypoint removal confirmation dialog
  const [removeConfirm, setRemoveConfirm] = useState(null); // { routeId, waypointId, salesPointName }
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    depot_id: '',
    route_type: 'delivery',
    corridor_meters: 100,
    color_hex: ROUTE_COLORS[0],
    notify_salespoints: true,
    notification_distance_meters: 500,
    active_days: [0, 1, 2, 3, 4],
    salespoint_ids: [],
    optimize_order: true,
  });
  
  // Main-route waypoint picker (replaces old dropdown form)
  const [showWaypointPicker, setShowWaypointPicker] = useState(null); // routeId

  // Subroute state
  const [subroutesByRoute, setSubroutesByRoute] = useState({});
  const [expandedSubrouteId, setExpandedSubrouteId] = useState(null);
  const [showSubrouteForm, setShowSubrouteForm] = useState(null); // routeId
  const [editingSubroute, setEditingSubroute] = useState(null);
  const [subrouteFormData, setSubrouteFormData] = useState({
    code: '', name: '', branch_type: 'spur', color_hex: '#F59E0B',
    corridor_meters: 100, independently_serviceable: true,
  });
  const [showSubrouteWaypointPicker, setShowSubrouteWaypointPicker] = useState(null); // subrouteId
  const [reorderedSubrouteWaypoints, setReorderedSubrouteWaypoints] = useState({});
  const [pendingSubrouteReorder, setPendingSubrouteReorder] = useState({});
  const [draggedSubrouteWaypoint, setDraggedSubrouteWaypoint] = useState(null);

  // Fetch data
  const fetchRoutes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await routesAPI.getAll();
      
      // Handle Django REST Framework pagination: {count, next, previous, results}
      let routesData = [];
      if (res.data) {
        if (Array.isArray(res.data)) {
          // Direct array response
          routesData = res.data;
        } else if (res.data.results && Array.isArray(res.data.results)) {
          // Paginated response
          routesData = res.data.results;
        } else if (res.data.routes && Array.isArray(res.data.routes)) {
          // Alternative structure
          routesData = res.data.routes;
        } else {
          console.warn('Routes API returned unexpected structure:', res.data);
        }
      }
      
      console.log(`Routes fetched: ${routesData.length} routes`, routesData.slice(0, 3));
      setRoutes(routesData);
      setError(null);
    } catch (err) {
      const errorMsg = 'Failed to fetch routes';
      setError(errorMsg);
      console.error(errorMsg, err.response?.data || err.message);
      setRoutes([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSubroutes = useCallback(async (routeId) => {
    try {
      const res = await subRoutesAPI.list(routeId);
      const data = res.data?.results || res.data || [];
      setSubroutesByRoute(prev => ({ ...prev, [routeId]: data }));
    } catch (err) {
      console.error('Failed to fetch subroutes:', err);
    }
  }, []);

  const fetchSalesPoints = useCallback(async () => {
    try {
      const res = await salesPointsAPI.getAll();
      const spData = res.data?.results || res.data || [];
      console.log('Sales points fetched:', spData);
      setSalesPoints(spData);
    } catch (err) {
      console.error('Failed to fetch sales points:', err);
    }
  }, []);

  const fetchDepots = useCallback(async () => {
    try {
      const res = await depotsAPI.getAll();
      const depotsData = res.data?.results || res.data || [];
      console.log('Depots fetched:', depotsData);
      setDepots(depotsData);
    } catch (err) {
      console.error('Failed to fetch depots:', err);
      // Set empty array on error so form still works
      setDepots([]);
    }
  }, []);

  useEffect(() => {
    fetchRoutes();
    fetchSalesPoints();
    fetchDepots();
  }, [fetchRoutes, fetchSalesPoints, fetchDepots]);

  // Route CRUD operations
  const handleCreateRoute = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await routesAPI.create(formData);
      setShowCreateForm(false);
      resetForm();
      fetchRoutes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create route');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRoute = async (e) => {
    e.preventDefault();
    if (!editingRoute) return;
    try {
      setLoading(true);
      await routesAPI.patch(editingRoute.id, formData);
      setEditingRoute(null);
      resetForm();
      fetchRoutes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update route');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRoute = async (id) => {
    if (!window.confirm('Are you sure you want to delete this route?')) return;
    try {
      setLoading(true);
      await routesAPI.delete(id);
      fetchRoutes();
    } catch (err) {
      setError('Failed to delete route');
    } finally {
      setLoading(false);
    }
  };

  const handleOptimizeRoute = async (route) => {
    try {
      setLoading(true);
      setError(null);
      
      // Save the current waypoint order before optimizing so we can revert
      const currentWaypoints = [...(route.waypoints || [])].sort((a, b) => a.sequence_order - b.sequence_order);
      if (currentWaypoints.length > 0) {
        const savedOrder = currentWaypoints.map(wp => wp.id);
        setSavedWaypointOrders(prev => ({ ...prev, [route.id]: savedOrder }));
      }
      
      await routesAPI.optimize(route.id, {
        name: route.name,
        code: route.code,
      });
      await fetchRoutes();
      // Re-fetch the expanded route to show updated waypoint order
      if (expandedRouteId === route.id) {
        try {
          const res = await routesAPI.get(route.id);
          const updated = res.data;
          setRoutes(prev => prev.map(r => r.id === route.id ? updated : r));
        } catch (_) { /* fetchRoutes already refreshed */ }
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || err.response?.data?.error || 'Failed to optimize route';
      console.error('Optimize route error:', err.response?.data || err.message);
      setError(typeof msg === 'string' ? msg : 'Failed to optimize route');
      // Remove saved order on failure since nothing changed
      setSavedWaypointOrders(prev => {
        const updated = { ...prev };
        delete updated[route.id];
        return updated;
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRevertOrder = async (routeId) => {
    const savedOrder = savedWaypointOrders[routeId];
    if (!savedOrder || savedOrder.length === 0) return;
    
    try {
      setLoading(true);
      setError(null);
      await routesAPI.reorderWaypoints(routeId, savedOrder);
      
      // Clear saved order after successful revert
      setSavedWaypointOrders(prev => {
        const updated = { ...prev };
        delete updated[routeId];
        return updated;
      });
      
      await fetchRoutes();
      // Re-fetch the expanded route to show reverted order
      if (expandedRouteId === routeId) {
        try {
          const res = await routesAPI.get(routeId);
          const updated = res.data;
          setRoutes(prev => prev.map(r => r.id === routeId ? updated : r));
        } catch (_) { /* fetchRoutes already refreshed */ }
      }
    } catch (err) {
      console.error('Revert order error:', err.response?.data || err.message);
      setError('Failed to revert waypoint order');
    } finally {
      setLoading(false);
    }
  };

  // Auto-assign different colors to all routes
  const handleAutoColorizeRoutes = async () => {
    if (!window.confirm('Assign different colors to all routes?')) return;
    try {
      setLoading(true);
      const updatePromises = routes.map((route, index) => {
        const colorIndex = index % ROUTE_COLORS.length;
        const newColor = ROUTE_COLORS[colorIndex];
        return routesAPI.patch(route.id, { color_hex: newColor });
      });
      await Promise.all(updatePromises);
      fetchRoutes();
    } catch (err) {
      setError('Failed to colorize routes');
    } finally {
      setLoading(false);
    }
  };

  // Waypoint operations
  const handleRemoveWaypoint = (routeId, waypointId, salesPointName) => {
    setRemoveConfirm({ routeId, waypointId, salesPointName });
  };

  const confirmRemoveWaypoint = async (deleteSalespoint) => {
    const { routeId, waypointId } = removeConfirm;
    setRemoveConfirm(null);
    try {
      setLoading(true);
      const res = await routesAPI.removeWaypoint(routeId, waypointId, deleteSalespoint);
      if (deleteSalespoint && res.data?.message) {
        setError(null);
        // Briefly surface the success message via the error banner (re-used as notification)
        // We flip it to a success style via a separate flag
        setSuccessMessage(res.data.message);
      }
      fetchRoutes();
    } catch (err) {
      setError(deleteSalespoint ? 'Failed to delete salespoint' : 'Failed to remove waypoint');
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, waypoint, routeId) => {
    setDraggedWaypoint({ ...waypoint, routeId });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', waypoint.id);
  };

  const handleDragOver = (e, targetWaypoint, routeId) => {
    e.preventDefault();
    if (!draggedWaypoint || draggedWaypoint.id === targetWaypoint.id) return;
    
    // Get current waypoints order for this route
    const route = routes.find(r => r.id === routeId);
    if (!route) return;
    
    const currentOrder = reorderedWaypoints[routeId] || [...(route.waypoints || [])].sort((a, b) => a.sequence_order - b.sequence_order);
    
    const draggedIndex = currentOrder.findIndex(w => w.id === draggedWaypoint.id);
    const targetIndex = currentOrder.findIndex(w => w.id === targetWaypoint.id);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    // Reorder the array
    const newOrder = [...currentOrder];
    const [removed] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, removed);
    
    // Update local state
    setReorderedWaypoints(prev => ({ ...prev, [routeId]: newOrder }));
    setPendingReorder(prev => ({ ...prev, [routeId]: true }));
  };

  const handleDragEnd = () => {
    setDraggedWaypoint(null);
  };

  const handleSaveOrder = async (routeId) => {
    const newOrder = reorderedWaypoints[routeId];
    if (!newOrder) return;
    
    const waypointIds = newOrder.map(w => w.id);
    
    try {
      setLoading(true);
      await routesAPI.reorderWaypoints(routeId, waypointIds);
      setPendingReorder(prev => ({ ...prev, [routeId]: false }));
      setReorderedWaypoints(prev => {
        const updated = { ...prev };
        delete updated[routeId];
        return updated;
      });
      fetchRoutes();
    } catch (err) {
      setError('Failed to save waypoint order');
      console.error('Reorder waypoints error:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReorder = (routeId) => {
    setReorderedWaypoints(prev => {
      const updated = { ...prev };
      delete updated[routeId];
      return updated;
    });
    setPendingReorder(prev => ({ ...prev, [routeId]: false }));
  };

  const getWaypointsForRoute = (route) => {
    if (reorderedWaypoints[route.id]) {
      return reorderedWaypoints[route.id];
    }
    return [...(route.waypoints || [])].sort((a, b) => a.sequence_order - b.sequence_order);
  };

  // Form helpers
  const resetForm = () => {
    // Auto-assign next color based on existing routes
    const nextColorIndex = routes.length % ROUTE_COLORS.length;
    const nextColor = ROUTE_COLORS[nextColorIndex];
    
    setFormData({
      name: '',
      code: '',
      description: '',
      depot_id: '',
      route_type: 'delivery',
      corridor_meters: 100,
      color_hex: nextColor,
      notify_salespoints: true,
      notification_distance_meters: 500,
      active_days: [0, 1, 2, 3, 4],
      salespoint_ids: [],
      optimize_order: true,
    });
  };

  const startEditing = (route) => {
    setEditingRoute(route);
    setFormData({
      name: route.name,
      code: route.code,
      description: route.description || '',
      depot_id: route.depot?.id || '',
      route_type: route.route_type || 'delivery',
      corridor_meters: route.corridor_meters || 100,
      color_hex: route.color_hex || '#2196F3',
      notify_salespoints: route.notify_salespoints ?? true,
      notification_distance_meters: route.notification_distance_meters || 500,
      active_days: route.active_days || [0, 1, 2, 3, 4],
      salespoint_ids: [],
      optimize_order: true,
    });
    setShowCreateForm(true);
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      active_days: prev.active_days.includes(day)
        ? prev.active_days.filter(d => d !== day)
        : [...prev.active_days, day].sort(),
    }));
  };

  const getRouteDetails = async (id) => {
    try {
      const res = await routesAPI.get(id);
      return res.data;
    } catch (err) {
      console.error('Failed to fetch route details:', err);
      return null;
    }
  };

  const handleExpandRoute = async (routeId) => {
    if (expandedRouteId === routeId) {
      setExpandedRouteId(null);
    } else {
      setExpandedRouteId(routeId);
      const details = await getRouteDetails(routeId);
      if (details) {
        setRoutes(prev => prev.map(r => r.id === routeId ? { ...r, ...details } : r));
      }
      fetchSubroutes(routeId);
    }
  };

  // Main-route bulk waypoint add (via picker)
  const handleAddWaypointsBulk = async (routeId, orderedIds) => {
    try {
      setLoading(true);
      await routesAPI.addWaypointsBulk(routeId, { salespoint_ids: orderedIds });
      setShowWaypointPicker(null);
      fetchRoutes();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add waypoints');
    } finally {
      setLoading(false);
    }
  };

  // Subroute CRUD
  const handleCreateSubroute = async (e, routeId) => {
    e.preventDefault();
    try {
      setLoading(true);
      await subRoutesAPI.create({ parent_route_id: routeId, ...subrouteFormData });
      setShowSubrouteForm(null);
      setEditingSubroute(null);
      fetchSubroutes(routeId);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.code?.[0] || 'Failed to create branch');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubroute = async (e) => {
    e.preventDefault();
    const routeId = editingSubroute.parent_route;
    try {
      setLoading(true);
      await subRoutesAPI.patch(editingSubroute.id, subrouteFormData);
      setShowSubrouteForm(null);
      setEditingSubroute(null);
      fetchSubroutes(routeId);
    } catch (err) {
      setError('Failed to update branch');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubroute = async (subroute) => {
    if (!window.confirm(`Delete branch "${subroute.code}"? This cannot be undone.`)) return;
    try {
      setLoading(true);
      await subRoutesAPI.delete(subroute.id);
      setSubroutesByRoute(prev => ({
        ...prev,
        [subroute.parent_route]: (prev[subroute.parent_route] || []).filter(s => s.id !== subroute.id),
      }));
    } catch (err) {
      setError('Failed to delete branch');
    } finally {
      setLoading(false);
    }
  };

  const startEditingSubroute = (subroute) => {
    setEditingSubroute(subroute);
    setSubrouteFormData({
      code: subroute.code,
      name: subroute.name || '',
      branch_type: subroute.branch_type || 'spur',
      color_hex: subroute.color_hex || '#F59E0B',
      corridor_meters: subroute.corridor_meters || 100,
      independently_serviceable: subroute.independently_serviceable ?? true,
    });
    setShowSubrouteForm(subroute.parent_route);
  };

  // Subroute waypoint management
  const handleAddSubrouteWaypoints = async (subrouteId, parentRouteId, orderedIds) => {
    try {
      setLoading(true);
      await subRoutesAPI.addWaypointsBulk(subrouteId, { salespoint_ids: orderedIds });
      setShowSubrouteWaypointPicker(null);
      fetchSubroutes(parentRouteId);
    } catch (err) {
      setError('Failed to add stops to branch');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSubrouteWaypoint = async (subrouteId, parentRouteId, waypointId) => {
    try {
      setLoading(true);
      await subRoutesAPI.removeWaypoint(subrouteId, waypointId);
      fetchSubroutes(parentRouteId);
    } catch (err) {
      setError('Failed to remove stop from branch');
    } finally {
      setLoading(false);
    }
  };

  const handleOptimizeSubroute = async (subrouteId, parentRouteId) => {
    try {
      setLoading(true);
      const res = await subRoutesAPI.optimize(subrouteId);
      setSubroutesByRoute(prev => ({
        ...prev,
        [parentRouteId]: (prev[parentRouteId] || []).map(s => s.id === subrouteId ? res.data : s),
      }));
    } catch (err) {
      setError('Failed to optimize branch order');
    } finally {
      setLoading(false);
    }
  };

  // Subroute waypoint drag-and-drop
  const handleSubrouteDragStart = (e, waypoint, subrouteId) => {
    setDraggedSubrouteWaypoint({ ...waypoint, subrouteId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSubrouteDragOver = (e, targetWaypoint, subrouteId, parentRouteId) => {
    e.preventDefault();
    if (!draggedSubrouteWaypoint || draggedSubrouteWaypoint.id === targetWaypoint.id) return;
    const subroutes = subroutesByRoute[parentRouteId] || [];
    const subroute = subroutes.find(s => s.id === subrouteId);
    if (!subroute) return;
    const currentOrder = reorderedSubrouteWaypoints[subrouteId] ||
      [...(subroute.waypoints || [])].sort((a, b) => a.sequence_order - b.sequence_order);
    const draggedIndex = currentOrder.findIndex(w => w.id === draggedSubrouteWaypoint.id);
    const targetIndex = currentOrder.findIndex(w => w.id === targetWaypoint.id);
    if (draggedIndex === -1 || targetIndex === -1) return;
    const newOrder = [...currentOrder];
    const [removed] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, removed);
    setReorderedSubrouteWaypoints(prev => ({ ...prev, [subrouteId]: newOrder }));
    setPendingSubrouteReorder(prev => ({ ...prev, [subrouteId]: true }));
  };

  const handleSaveSubrouteOrder = async (subrouteId, parentRouteId) => {
    const newOrder = reorderedSubrouteWaypoints[subrouteId];
    if (!newOrder) return;
    try {
      setLoading(true);
      await subRoutesAPI.reorderWaypoints(subrouteId, newOrder.map(w => w.id));
      setPendingSubrouteReorder(prev => ({ ...prev, [subrouteId]: false }));
      setReorderedSubrouteWaypoints(prev => { const u = { ...prev }; delete u[subrouteId]; return u; });
      fetchSubroutes(parentRouteId);
    } catch (err) {
      setError('Failed to save branch stop order');
    } finally {
      setLoading(false);
    }
  };

  const getSubrouteWaypoints = (subroute) => {
    if (reorderedSubrouteWaypoints[subroute.id]) return reorderedSubrouteWaypoints[subroute.id];
    return [...(subroute.waypoints || [])].sort((a, b) => a.sequence_order - b.sequence_order);
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h3>Routes</h3>
        <div className={styles.headerActions}>
          {routes.length > 0 && (
            <button 
              className={styles.iconBtn}
              onClick={handleAutoColorizeRoutes}
              title="Auto-assign colors to routes"
            >
              🎨
            </button>
          )}
          <button 
            className={styles.iconBtn}
            onClick={fetchRoutes}
            title="Refresh"
          >
            <IoRefreshOutline size={18} />
          </button>
          <button 
            className={styles.addBtn}
            onClick={() => { setShowCreateForm(true); setEditingRoute(null); resetForm(); }}
          >
            <IoAddOutline size={18} />
            New Route
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className={styles.success}>
          {successMessage}
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className={styles.formOverlay}>
          <form 
            className={styles.form} 
            onSubmit={editingRoute ? handleUpdateRoute : handleCreateRoute}
          >
            <div className={styles.formHeader}>
              <h4>{editingRoute ? 'Edit Route' : 'Create Route'}</h4>
              <button 
                type="button" 
                className={styles.closeBtn}
                onClick={() => { setShowCreateForm(false); setEditingRoute(null); }}
              >
                <IoCloseOutline size={20} />
              </button>
            </div>

            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="North Kampala Route"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={e => setFormData({ ...formData, code: e.target.value })}
                  placeholder="R001"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>Depot *</label>
                <select
                  value={formData.depot_id}
                  onChange={e => setFormData({ ...formData, depot_id: e.target.value })}
                  required
                >
                  <option value="">Select depot...</option>
                  {depots.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Route Type</label>
                <select
                  value={formData.route_type}
                  onChange={e => setFormData({ ...formData, route_type: e.target.value })}
                >
                  {ROUTE_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Route Color</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {ROUTE_COLORS.map(color => (
                    <div
                      key={color}
                      onClick={() => setFormData({ ...formData, color_hex: color })}
                      style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: color,
                        borderRadius: '4px',
                        cursor: 'pointer',
                        border: formData.color_hex === color ? '3px solid #000' : '2px solid #ddd',
                        transition: 'all 0.2s',
                      }}
                      title={color}
                    />
                  ))}
                  <input
                    type="color"
                    value={formData.color_hex}
                    onChange={e => setFormData({ ...formData, color_hex: e.target.value })}
                    style={{ width: '40px', height: '32px', cursor: 'pointer' }}
                    title="Custom color"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Corridor (meters)</label>
                <input
                  type="number"
                  value={formData.corridor_meters}
                  onChange={e => setFormData({ ...formData, corridor_meters: parseInt(e.target.value) })}
                  min="0"
                />
              </div>

              <div className={styles.formGroupFull}>
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description..."
                  rows={2}
                />
              </div>

              <div className={styles.formGroupFull}>
                <label>Active Days</label>
                <div className={styles.daysGrid}>
                  {DAYS_OF_WEEK.map(day => (
                    <button
                      key={day.value}
                      type="button"
                      className={`${styles.dayBtn} ${formData.active_days.includes(day.value) ? styles.active : ''}`}
                      onClick={() => toggleDay(day.value)}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.formGroupFull}>
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={formData.notify_salespoints}
                    onChange={e => setFormData({ ...formData, notify_salespoints: e.target.checked })}
                  />
                  Notify salespoints on approach
                </label>
              </div>

              {formData.notify_salespoints && (
                <div className={styles.formGroup}>
                  <label>Notification Distance (m)</label>
                  <input
                    type="number"
                    value={formData.notification_distance_meters}
                    onChange={e => setFormData({ ...formData, notification_distance_meters: parseInt(e.target.value) })}
                    min="0"
                  />
                </div>
              )}

              {!editingRoute && (
                <div className={styles.formGroupFull}>
                  <label>
                    Initial Salespoints (optional)
                    {formData.salespoint_ids.length > 0 && (
                      <span className={styles.selectionCount}> — {formData.salespoint_ids.length} selected</span>
                    )}
                  </label>
                  <SalespointPicker
                    salesPoints={salesPoints}
                    excludeIds={new Set()}
                    onSubmit={(ids) => setFormData({ ...formData, salespoint_ids: ids })}
                  />
                  {formData.salespoint_ids.length > 0 && (
                    <small className={styles.pickerHint}>
                      {formData.salespoint_ids.length} salespoint{formData.salespoint_ids.length !== 1 ? 's' : ''} will be added in marked order.{' '}
                      <button type="button" className={styles.linkBtn} onClick={() => setFormData({ ...formData, salespoint_ids: [] })}>Clear</button>
                    </small>
                  )}
                </div>
              )}

              {!editingRoute && (
                <div className={styles.formGroupFull}>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={formData.optimize_order}
                      onChange={e => setFormData({ ...formData, optimize_order: e.target.checked })}
                    />
                    Optimize waypoint order automatically
                  </label>
                </div>
              )}
            </div>

            <div className={styles.formActions}>
              <button 
                type="button" 
                className={styles.cancelBtn}
                onClick={() => { setShowCreateForm(false); setEditingRoute(null); }}
              >
                Cancel
              </button>
              <button type="submit" className={styles.submitBtn} disabled={loading}>
                <IoSaveOutline size={16} />
                {editingRoute ? 'Update' : 'Create'} Route
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Routes List */}
      <div className={styles.routesList}>
        {loading && routes.length === 0 ? (
          <div className={styles.loading}>Loading routes...</div>
        ) : !Array.isArray(routes) || routes.length === 0 ? (
          <div className={styles.empty}>No routes found. Create one to get started!</div>
        ) : (
          routes.map(route => (
            <div 
              key={route.id} 
              className={`${styles.routeCard} ${selectedRouteIds.includes(route.id) ? styles.selected : ''}`}
            >
              <div 
                className={styles.routeHeader}
                onClick={() => {
                  handleExpandRoute(route.id);
                  onRouteSelect?.(route);
                }}
              >
                <div 
                  className={styles.routeColor} 
                  style={{ backgroundColor: route.color_hex || '#2196F3' }}
                />
                <div className={styles.routeInfo}>
                  <div className={styles.routeName}>
                    {route.name}
                    {selectedRouteIds.includes(route.id) && (
                      <IoCheckmarkOutline 
                        size={16} 
                        style={{ color: '#4caf50', marginLeft: '8px' }}
                        title="Selected on map"
                      />
                    )}
                  </div>
                  <div className={styles.routeMeta}>
                    <span className={styles.routeCode}>{route.code}</span>
                    <span className={styles.routeType}>{route.route_type}</span>
                    <span className={styles.waypointCount}>
                      {route.waypoint_count || route.waypoints?.length || 0} stops
                    </span>
                  </div>
                </div>
                <div className={styles.routeActions}>
                  <button 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      onRouteSelect?.(route);
                    }}
                    title={selectedRouteIds.includes(route.id) ? 'Remove from map' : 'Show on map'}
                    className={selectedRouteIds.includes(route.id) ? styles.selectedBtn : ''}
                  >
                    <IoLocationOutline size={16} />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); startEditing(route); }}
                    title="Edit"
                  >
                    <IoCreateOutline size={16} />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleOptimizeRoute(route); }}
                    title="Optimize"
                  >
                    <IoFlashOutline size={16} />
                  </button>
                  {savedWaypointOrders[route.id] && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleRevertOrder(route.id); }}
                      title="Revert to previous order"
                      className={styles.revertBtn}
                    >
                      <IoArrowUndoOutline size={16} />
                    </button>
                  )}
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleDeleteRoute(route.id); }}
                    title="Delete"
                    className={styles.deleteBtn}
                  >
                    <IoTrashOutline size={16} />
                  </button>
                </div>
              </div>

              {/* Expanded View */}
              {expandedRouteId === route.id && (
                <div className={styles.routeDetails}>
                  <div className={styles.detailsHeader}>
                    <h5>Waypoints</h5>
                    <button
                      className={styles.addWaypointBtn}
                      onClick={() => setShowWaypointPicker(route.id)}
                    >
                      <IoAddOutline size={14} />
                      Add Waypoints
                    </button>
                  </div>

                  {/* Waypoints List with Drag and Drop */}
                  <div className={styles.waypointsList}>
                    {getWaypointsForRoute(route).length === 0 ? (
                      <div className={styles.noWaypoints}>No waypoints yet</div>
                    ) : (
                      <>
                        {pendingReorder[route.id] && (
                          <div className={styles.reorderActions}>
                            <button 
                              className={styles.saveOrderBtn}
                              onClick={() => handleSaveOrder(route.id)}
                              disabled={loading}
                            >
                              <IoCheckmarkOutline size={14} />
                              Save Order
                            </button>
                            <button 
                              className={styles.cancelOrderBtn}
                              onClick={() => handleCancelReorder(route.id)}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                        {getWaypointsForRoute(route).map((wp, idx) => (
                          <div 
                            key={wp.id} 
                            className={`${styles.waypointItem} ${draggedWaypoint?.id === wp.id ? styles.dragging : ''}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, wp, route.id)}
                            onDragOver={(e) => handleDragOver(e, wp, route.id)}
                            onDragEnd={handleDragEnd}
                          >
                            <div className={styles.dragHandle}>
                              <IoReorderThreeOutline size={18} />
                            </div>
                            <div className={styles.waypointOrder}>{idx + 1}</div>
                            <IoLocationOutline className={styles.waypointIcon} />
                            <div className={styles.waypointInfo}>
                              <div className={styles.waypointName}>
                                {wp.salespoint?.name || 'Unknown'}
                              </div>
                              <div className={styles.waypointMeta}>
                                {wp.expected_stop_duration_minutes}min
                                {wp.is_required && <span className={styles.required}>Required</span>}
                              </div>
                            </div>
                            <div className={styles.waypointActions}>
                              <button
                                onClick={() => handleRemoveWaypoint(route.id, wp.id, wp.salespoint?.name)}
                                className={styles.removeBtn}
                              >
                                <IoTrashOutline size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>

                  {/* Route Stats */}
                  <div className={styles.routeStats}>
                    <div className={styles.stat}>
                      <label>Distance</label>
                      <span>{route.total_distance_km || '—'} km</span>
                    </div>
                    <div className={styles.stat}>
                      <label>Duration</label>
                      <span>{route.estimated_duration_minutes || '—'} min</span>
                    </div>
                    <div className={styles.stat}>
                      <label>Vehicles</label>
                      <span>{route.assigned_vehicles_count || 0}</span>
                    </div>
                  </div>

                  {/* Branches / Sub-routes */}
                  <div className={styles.branchesSection}>
                    <div className={styles.branchesSectionHeader}>
                      <span>Branches ({(subroutesByRoute[route.id] || []).length})</span>
                      <button
                        className={styles.addWaypointBtn}
                        onClick={() => {
                          setEditingSubroute(null);
                          setSubrouteFormData({ code: '', name: '', branch_type: 'spur', color_hex: '#F59E0B', corridor_meters: 100, independently_serviceable: true });
                          setShowSubrouteForm(route.id);
                        }}
                      >
                        <IoAddOutline size={14} /> New Branch
                      </button>
                    </div>

                    {(subroutesByRoute[route.id] || []).map(branch => (
                      <div
                        key={branch.id}
                        className={styles.branchCard}
                        style={{ borderLeftColor: branch.color_hex || '#F59E0B' }}
                      >
                        <div className={styles.branchCardHeader}>
                          <span
                            className={styles.branchColorDot}
                            style={{ backgroundColor: branch.color_hex || '#F59E0B' }}
                          />
                          <span className={styles.branchCode}>{branch.code}</span>
                          {branch.name && <span className={styles.branchName}>{branch.name}</span>}
                          <span className={styles.branchMeta}>{branch.waypoint_count || 0} stops · {branch.branch_type}</span>
                          <div className={styles.branchActions}>
                            <button
                              title="Expand/collapse"
                              onClick={() => setExpandedSubrouteId(expandedSubrouteId === branch.id ? null : branch.id)}
                            >
                              {expandedSubrouteId === branch.id ? '▲' : '▼'}
                            </button>
                            <button title="Edit branch" onClick={() => startEditingSubroute(branch)}>
                              <IoCreateOutline size={14} />
                            </button>
                            <button
                              title="Delete branch"
                              className={styles.deleteBtn}
                              onClick={() => handleDeleteSubroute(branch)}
                            >
                              <IoTrashOutline size={14} />
                            </button>
                          </div>
                        </div>

                        {expandedSubrouteId === branch.id && (
                          <div className={styles.branchBody}>
                            {pendingSubrouteReorder[branch.id] && (
                              <div className={styles.reorderActions}>
                                <button
                                  className={styles.saveOrderBtn}
                                  onClick={() => handleSaveSubrouteOrder(branch.id, route.id)}
                                  disabled={loading}
                                >
                                  <IoCheckmarkOutline size={14} /> Save Order
                                </button>
                                <button
                                  className={styles.cancelOrderBtn}
                                  onClick={() => {
                                    setReorderedSubrouteWaypoints(prev => { const u = { ...prev }; delete u[branch.id]; return u; });
                                    setPendingSubrouteReorder(prev => ({ ...prev, [branch.id]: false }));
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                            {getSubrouteWaypoints(branch).length === 0 ? (
                              <div className={styles.noWaypoints}>No stops yet</div>
                            ) : (
                              getSubrouteWaypoints(branch).map((wp, idx) => (
                                <div
                                  key={wp.id}
                                  className={`${styles.waypointItem} ${draggedSubrouteWaypoint?.id === wp.id ? styles.dragging : ''}`}
                                  draggable
                                  onDragStart={(e) => handleSubrouteDragStart(e, wp, branch.id)}
                                  onDragOver={(e) => handleSubrouteDragOver(e, wp, branch.id, route.id)}
                                  onDragEnd={() => setDraggedSubrouteWaypoint(null)}
                                >
                                  <div className={styles.dragHandle}><IoReorderThreeOutline size={18} /></div>
                                  <div className={styles.waypointOrder}>{idx + 1}</div>
                                  <IoLocationOutline className={styles.waypointIcon} />
                                  <div className={styles.waypointInfo}>
                                    <div className={styles.waypointName}>{wp.salespoint?.name || 'Unknown'}</div>
                                  </div>
                                  <div className={styles.waypointActions}>
                                    <button
                                      className={styles.removeBtn}
                                      onClick={() => handleRemoveSubrouteWaypoint(branch.id, route.id, wp.id)}
                                    >
                                      <IoTrashOutline size={14} />
                                    </button>
                                  </div>
                                </div>
                              ))
                            )}
                            <div className={styles.branchFooter}>
                              <button
                                className={styles.addWaypointBtn}
                                onClick={() => setShowSubrouteWaypointPicker(branch.id)}
                              >
                                <IoAddOutline size={13} /> Add Stops
                              </button>
                              <button
                                className={styles.addWaypointBtn}
                                onClick={() => handleOptimizeSubroute(branch.id, route.id)}
                                disabled={loading}
                                title="Optimize stop order by proximity"
                              >
                                <IoFlashOutline size={13} /> Optimize
                              </button>
                            </div>

                            {/* Branch waypoint picker (inline) */}
                            {showSubrouteWaypointPicker === branch.id && (
                              <div className={styles.inlinePicker}>
                                <SalespointPicker
                                  salesPoints={salesPoints}
                                  excludeIds={new Set((branch.waypoints || []).map(w => w.salespoint?.id).filter(Boolean))}
                                  onSubmit={(ids) => handleAddSubrouteWaypoints(branch.id, route.id, ids)}
                                  onCancel={() => setShowSubrouteWaypointPicker(null)}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Main-route waypoint picker modal */}
      {showWaypointPicker && (
        <div className={styles.formOverlay}>
          <div className={styles.form}>
            <div className={styles.formHeader}>
              <h4>Add Waypoints</h4>
              <button type="button" className={styles.closeBtn} onClick={() => setShowWaypointPicker(null)}>
                <IoCloseOutline size={20} />
              </button>
            </div>
            <p className={styles.pickerHint}>Search and mark salespoints in the order you want them added.</p>
            <SalespointPicker
              salesPoints={salesPoints}
              excludeIds={new Set(
                (routes.find(r => r.id === showWaypointPicker)?.waypoints || []).map(w => w.salespoint?.id).filter(Boolean)
              )}
              onSubmit={(ids) => handleAddWaypointsBulk(showWaypointPicker, ids)}
              onCancel={() => setShowWaypointPicker(null)}
            />
          </div>
        </div>
      )}

      {/* Subroute create/edit modal */}
      {showSubrouteForm && (
        <div className={styles.formOverlay}>
          <form
            className={styles.form}
            onSubmit={editingSubroute ? handleUpdateSubroute : (e) => handleCreateSubroute(e, showSubrouteForm)}
          >
            <div className={styles.formHeader}>
              <h4>{editingSubroute ? 'Edit Branch' : 'New Branch'}</h4>
              <button type="button" className={styles.closeBtn} onClick={() => { setShowSubrouteForm(null); setEditingSubroute(null); }}>
                <IoCloseOutline size={20} />
              </button>
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label>Code *</label>
                <input
                  type="text"
                  value={subrouteFormData.code}
                  onChange={e => setSubrouteFormData({ ...subrouteFormData, code: e.target.value })}
                  placeholder="BR-001"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>Name</label>
                <input
                  type="text"
                  value={subrouteFormData.name}
                  onChange={e => setSubrouteFormData({ ...subrouteFormData, name: e.target.value })}
                  placeholder="Optional name"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Branch Type</label>
                <select
                  value={subrouteFormData.branch_type}
                  onChange={e => setSubrouteFormData({ ...subrouteFormData, branch_type: e.target.value })}
                >
                  {BRANCH_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Corridor (meters)</label>
                <input
                  type="number"
                  value={subrouteFormData.corridor_meters}
                  onChange={e => setSubrouteFormData({ ...subrouteFormData, corridor_meters: parseInt(e.target.value) })}
                  min="0"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Color</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {ROUTE_COLORS.map(color => (
                    <div
                      key={color}
                      onClick={() => setSubrouteFormData({ ...subrouteFormData, color_hex: color })}
                      style={{
                        width: '24px', height: '24px', backgroundColor: color, borderRadius: '4px',
                        cursor: 'pointer',
                        border: subrouteFormData.color_hex === color ? '3px solid #000' : '2px solid #ddd',
                      }}
                    />
                  ))}
                  <input
                    type="color"
                    value={subrouteFormData.color_hex}
                    onChange={e => setSubrouteFormData({ ...subrouteFormData, color_hex: e.target.value })}
                    style={{ width: '36px', height: '28px', cursor: 'pointer' }}
                  />
                </div>
              </div>
              <div className={styles.formGroupFull}>
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={subrouteFormData.independently_serviceable}
                    onChange={e => setSubrouteFormData({ ...subrouteFormData, independently_serviceable: e.target.checked })}
                  />
                  Can be serviced independently (separate vehicle)
                </label>
              </div>
            </div>
            <div className={styles.formActions}>
              <button type="button" className={styles.cancelBtn} onClick={() => { setShowSubrouteForm(null); setEditingSubroute(null); }}>
                Cancel
              </button>
              <button type="submit" className={styles.submitBtn} disabled={loading}>
                <IoSaveOutline size={16} />
                {editingSubroute ? 'Update' : 'Create'} Branch
              </button>
            </div>
          </form>
        </div>
      )}

      {removeConfirm && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialog}>
            <p className={styles.dialogTitle}>Remove waypoint</p>
            <p className={styles.dialogBody}>
              What do you want to do with <strong>{removeConfirm.salesPointName || 'this salespoint'}</strong>?
            </p>
            <div className={styles.dialogActions}>
              <button className={styles.dialogBtnSecondary} onClick={() => setRemoveConfirm(null)}>
                Cancel
              </button>
              <button className={styles.dialogBtnWarning} onClick={() => confirmRemoveWaypoint(false)}>
                Remove from route
              </button>
              <button className={styles.dialogBtnDanger} onClick={() => confirmRemoveWaypoint(true)}>
                Delete salespoint
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
